import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./AdminPanel.css";

function AnnouncementsManager() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    img_urls: [], // Birden fazla görsel için liste
  });

  const quillModules = {
    toolbar: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline"],
      ["link", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };

  useEffect(() => {
    const fetchAnnouncements = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("announcements")
        .select("*, announcement_images(img_url)")
        .order("id", { ascending: false });

      if (error) {
        console.error("Duyurular çekilirken bir hata oluştu:", error);
      } else {
        setAnnouncements(
          (data || []).map((announcement) => ({
            ...announcement,
            img_urls: (announcement.announcement_images || []).map((img) => img.img_url),
          }))
        );
      }
      setLoading(false);
    };

    fetchAnnouncements();
  }, []);

  const addAnnouncement = async () => {
    const { title, description, img_urls } = formData;

    if (!title || !description) {
      alert("Başlık ve açıklama boş bırakılamaz.");
      return;
    }

    const { data, error } = await supabase
      .from("announcements")
      .insert([{ title, description }])
      .select();

    if (error || !data || data.length === 0) {
      console.error("Duyuru eklenirken bir hata oluştu:", error);
      alert("Duyuru eklenirken bir hata oluştu.");
      return;
    }

    const newAnnouncement = data[0];

    if (img_urls.length > 0) {
      const imageInsertData = img_urls.map((url) => ({
        announcement_id: newAnnouncement.id,
        img_url: url,
      }));

      const { error: imageError } = await supabase
        .from("announcement_images")
        .insert(imageInsertData);

      if (imageError) {
        console.error("Görseller eklenirken bir hata oluştu:", imageError);
        alert("Görseller eklenirken bir hata oluştu.");
        return;
      }
    }

    setAnnouncements([{ ...newAnnouncement, img_urls }, ...announcements]);
    setFormData({ title: "", description: "", img_urls: [] });
  };

  const updateAnnouncement = async () => {
    const { title, description, img_urls } = formData;

    const { error } = await supabase
      .from("announcements")
      .update({ title, description })
      .eq("id", editingId);

    if (error) {
      console.error("Duyuru güncellenirken bir hata oluştu:", error);
      return;
    }

    // Görselleri güncelle
    await supabase.from("announcement_images").delete().eq("announcement_id", editingId);
    const imageInsertData = img_urls.map((url) => ({
      announcement_id: editingId,
      img_url: url,
    }));
    await supabase.from("announcement_images").insert(imageInsertData);

    setAnnouncements(
      announcements.map((announcement) =>
        announcement.id === editingId
          ? { ...announcement, title, description, img_urls }
          : announcement
      )
    );
    setEditingId(null);
    setFormData({ title: "", description: "", img_urls: [] });
  };

  const addImage = () => {
    const newImageUrl = prompt("Görsel URL'sini girin:");
    if (newImageUrl) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        img_urls: [...prevFormData.img_urls, newImageUrl],
      }));
    }
  };

  const deleteImage = async (index, imgUrl) => {
    if (!editingId) {
      // Yeni eklenen ancak kaydedilmemiş görselleri yalnızca formData'dan sil
      setFormData((prevFormData) => ({
        ...prevFormData,
        img_urls: prevFormData.img_urls.filter((_, i) => i !== index),
      }));
      return;
    }

    // Supabase'den görseli sil
    try {
      const { error } = await supabase
        .from("announcement_images")
        .delete()
        .eq("img_url", imgUrl)
        .eq("announcement_id", editingId);

      if (error) {
        console.error("Görsel silinirken hata oluştu:", error);
        alert("Görsel silinirken bir hata oluştu.");
        return;
      }

      // FormData'dan görseli sil
      setFormData((prevFormData) => ({
        ...prevFormData,
        img_urls: prevFormData.img_urls.filter((_, i) => i !== index),
      }));
    } catch (err) {
      console.error("Beklenmedik bir hata oluştu:", err);
      alert("Beklenmedik bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  const deleteAnnouncement = async (id) => {
    const confirmed = window.confirm(
      "Bu duyuruyu silmek istediğinize emin misiniz?"
    );

    if (!confirmed) return;

    try {
      await supabase.from("announcement_images").delete().eq("announcement_id", id);
      const { error } = await supabase.from("announcements").delete().eq("id", id);

      if (error) {
        console.error("Duyuru silinirken hata oluştu:", error);
        alert("Duyuru silinirken bir hata oluştu.");
      } else {
        setAnnouncements(announcements.filter((announcement) => announcement.id !== id));
      }
    } catch (err) {
      console.error("Beklenmedik bir hata oluştu:", err);
      alert("Beklenmedik bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  const handleDescriptionChange = (value) => {
    setFormData({ ...formData, description: value });
  };

  if (loading) return <p>Yükleniyor...</p>;

  return (
    <div className="admin-panel">
      <h1>Admin Panel - Duyurular</h1>

      {/* Yeni Duyuru Ekleme */}
      <div className="add-announcement">
        <h3>Yeni Duyuru Ekle</h3>
        <input
          type="text"
          placeholder="Başlık"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <ReactQuill
          value={formData.description}
          onChange={handleDescriptionChange}
          modules={quillModules}
          placeholder="Açıklama"
        />
        <div className="image-list">
          {formData.img_urls.map((image, index) => (
            <div key={index} className="image-preview">
              <img src={image} alt={`Görsel ${index + 1}`} />
              <button
                onClick={() => deleteImage(index, image)}
                className="delete-image-button"
              >
                Sil
              </button>
            </div>
          ))}
          <button onClick={addImage}>Görsel Ekle</button>
        </div>
        <button onClick={editingId ? updateAnnouncement : addAnnouncement}>
          {editingId ? "Güncelle" : "Ekle"}
        </button>
      </div>

      {/* Mevcut Duyurular */}
      <div className="announcement-list">
        {announcements.map((announcement) => (
          <div key={announcement.id} className="announcement-item">
            {editingId === announcement.id ? (
              <>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Başlık"
                />
                <ReactQuill
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  modules={quillModules}
                  placeholder="Açıklama"
                />
                <div className="image-list">
                  {formData.img_urls.map((image, index) => (
                    <div key={index} className="image-preview">
                      <img src={image} alt={`Görsel ${index + 1}`} />
                      <button
                        onClick={() => deleteImage(index, image)}
                        className="delete-image-button"
                      >
                        Görsel Sil
                      </button>
                    </div>
                  ))}
                  <button onClick={addImage}>Görsel Ekle</button>
                </div>
                <button onClick={updateAnnouncement}>Kaydet</button>
                <button onClick={() => setEditingId(null)}>İptal</button>
              </>
            ) : (
              <>
                <h3>{announcement.title}</h3>
                <div
                  dangerouslySetInnerHTML={{ __html: announcement.description }}
                ></div>
                <div className="image-list">
                  {announcement.img_urls.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`Duyuru Görsel ${index + 1}`}
                      className="announcement-image"
                    />
                  ))}
                </div>
                <button
                  onClick={() => {
                    setEditingId(announcement.id);
                    setFormData({
                      title: announcement.title,
                      description: announcement.description,
                      img_urls: announcement.img_urls || [],
                    });
                  }}
                >
                  Düzenle
                </button>
                <button
                  className="delete-button"
                  onClick={() => deleteAnnouncement(announcement.id)}
                >
                  Sil
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AnnouncementsManager;
